import { gql } from '@apollo/client'
import { CARS_VEHICLES_CATEGORIES } from '@kijiji/category'
import {
  type AttributeFilter,
  type AttributeFilterInput,
  type FilterGroup,
  type GetSearchResultsPageByUrlQuery,
  type RangeFilterInput,
  type SearchQueryInput,
  type SearchUrlInput,
  useGetSeoUrlLazyQuery,
} from '@kijiji/generated/graphql-types'
import AutosPriceIcon from '@kijiji/icons/src/icons/AutosPrice'
import KmSimpleIcon from '@kijiji/icons/src/icons/KmSimple'
import { formatWholeNumber } from '@kijiji/number/formatWholeNumber'
import debounce from 'lodash/debounce'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import {
  type ChangeEvent,
  type FC,
  type FocusEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useTheme } from 'styled-components'

import { FormWrapper, GridItemWrapper } from '@/components/brp/top-cars-banner/find-car/styled'
import { TRANSLATION_KEYS } from '@/constants/localization'
import { FIND_CAR_FILTERS_DEBOUNCE } from '@/constants/others'
import { ROUTE_LOCALE } from '@/domain/locale'
import { getResults } from '@/domain/srp/getResults'
import { GPT_KEYS } from '@/features/advertisement/constants/gpt'
import { ATTRIBUTES } from '@/features/attributes/constants/attributes'
import { useGetLocation } from '@/hooks/location/useGetLocation'
import { useLocale } from '@/hooks/useLocale'
import { initializeApollo } from '@/lib/apollo/apolloClient'
import { trackEvent } from '@/lib/ga'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'
import { Button } from '@/ui/atoms/button'
import { type SelectItem, Select } from '@/ui/molecules/select'
import { TextField } from '@/ui/molecules/text-field'

type FindCarProps = {
  srpData: GetSearchResultsPageByUrlQuery['searchResultsPageByUrl'] | undefined
}

const GET_SEARCH_RESULTS_COUNT_WITH_CONTROLS = gql`
  query SearchResultsPage($by: SearchResultsPageInputBy!, $pagination: PaginationInputV2!) {
    searchResultsPage(by: $by, pagination: $pagination) {
      pagination {
        totalCount
      }
      controls {
        filtering {
          name
          filters {
            type
            name
            label
            parentName
            ... on AttributeFilter {
              type
              name
              label
              values {
                label
                value
                parentValue
                totalResults
                seoUrl
              }
            }
          }
        }
      }
    }
  }
`

const GET_SEARCH_RESULTS_COUNT = gql`
  query SearchResultsPage($by: SearchResultsPageInputBy!, $pagination: PaginationInputV2!) {
    searchResultsPage(by: $by, pagination: $pagination) {
      pagination {
        totalCount
      }
    }
  }
`

export const FindCar: FC<FindCarProps> = ({ srpData }) => {
  const { t } = useTranslation(TRANSLATION_KEYS.SRP)
  const [selectedCarMake, setSelectedCarMake] = useState<SelectItem>()
  const [carModels, setCarModels] = useState<SelectItem[]>([
    {
      label: 'Select Make first',
      value: '',
      disabled: true,
    },
  ])
  const [selectedCarModel, setSelectedCarModel] = useState<SelectItem>()
  const [maxPrice, setMaxPrice] = useState<number>()
  const [maxKilometers, setMaxKilometers] = useState<number>()
  const theme = useTheme()
  const [fetchSeoUrl] = useGetSeoUrlLazyQuery()
  const { push } = useRouter()
  const { routeLocale } = useLocale()
  const apolloClient = initializeApollo()
  const { location } = useGetLocation()

  const [totalResults, setTotalResults] = useState<number>(0)
  const [isFetchingResults, setIsFetchingResults] = useState(false)
  const [searchQuery, setSearchQuery] = useState<SearchUrlInput['searchQuery']>({
    categoryId: CARS_VEHICLES_CATEGORIES.CARS_TRUCKS,
    location: { id: location.id },
  })

  const { controls } = srpData || {}
  const filterGroups = useMemo(() => controls?.filtering ?? [], [controls])
  const makeModelFilters = filterGroups.find((fg) => fg.name === 'make')
    ?.filters as AttributeFilter[]
  const makeOptions =
    makeModelFilters?.find((filter) => filter.name === ATTRIBUTES.CAR_MAKE)?.values || []

  useEffect(() => {
    if (!totalResults && srpData) {
      setTotalResults(srpData.pagination.totalCount)
    }
  }, [srpData])

  useEffect(() => {
    setSearchQuery({
      categoryId: CARS_VEHICLES_CATEGORIES.CARS_TRUCKS,
      location: { id: location.id },
      attributeFilters: [
        {
          filterName: GPT_KEYS.CARMAKE_ATTRIBUTE_ID,
          values:
            typeof selectedCarMake?.value === 'string' && selectedCarMake?.value
              ? [selectedCarMake.value]
              : [],
        },
        {
          filterName: GPT_KEYS.CARMODEL_ATTRIBUTE_ID,
          values:
            typeof selectedCarModel?.value === 'string' && selectedCarModel?.value
              ? [selectedCarModel.value]
              : [],
        },
      ],
      rangeFilters: [
        {
          filterName: GPT_KEYS.CARMILEAGE_ATTRIBUTE_ID,
          minValue: 0,
          maxValue: maxKilometers || null,
        },
        {
          filterName: GPT_KEYS.PRICE_LABEL,
          minValue: 0,
          maxValue: maxPrice ? maxPrice * 100 : null,
        },
      ],
    })
  }, [selectedCarMake, selectedCarModel, maxPrice, maxKilometers, location.id])

  const getTrackingLabel = ({
    make,
    model,
    price,
    km,
  }: {
    make?: string | number
    model?: string | number
    price?: string | number
    km?: string | number
  }) => {
    return `page=brp_landing_page;section=find_your_next_car;make=${make ?? selectedCarMake?.value ?? ''};model=${model ?? selectedCarModel?.value ?? ''};price=${price ?? maxPrice ?? ''};km=${km ?? maxKilometers ?? ''}`
  }

  const fetchTotalResults = async ({
    searchQuery,
    attributeFilter,
    rangeFilter,
    withControls,
  }: {
    searchQuery: SearchQueryInput
    attributeFilter?: AttributeFilterInput
    rangeFilter?: RangeFilterInput
    withControls?: boolean
  }) => {
    const otherAttributeFilters =
      attributeFilter?.filterName === ATTRIBUTES.CAR_MAKE
        ? []
        : (searchQuery.attributeFilters || [])?.filter(
            (x) => x.filterName !== attributeFilter?.filterName
          )
    const otherRangeFilters = (searchQuery.rangeFilters || [])?.filter(
      (x) => x.filterName !== rangeFilter?.filterName
    )

    const query = withControls ? GET_SEARCH_RESULTS_COUNT_WITH_CONTROLS : GET_SEARCH_RESULTS_COUNT
    const result = await getResults(
      {
        ...searchQuery,
        attributeFilters: otherAttributeFilters.concat(attributeFilter || []),
        rangeFilters: otherRangeFilters.concat(rangeFilter || []),
      },
      apolloClient,
      query
    )
      .then((res) => res.data.searchResultsPage)
      .catch(() => null)

    return result
  }

  const handleChangeCarMake = async (selectItem?: SelectItem) => {
    setIsFetchingResults(true)

    setSelectedCarModel(undefined)
    setSelectedCarMake(selectItem)

    trackEvent({
      action: GA_EVENT.FilterSelectBegin,
      label: getTrackingLabel({ make: selectItem?.value }),
    })

    const searchResultsPage = await fetchTotalResults({
      searchQuery,
      attributeFilter: {
        filterName: GPT_KEYS.CARMAKE_ATTRIBUTE_ID,
        values: [selectItem?.value as string],
      },
      withControls: true,
    })

    if (searchResultsPage) {
      const filterGroups = searchResultsPage.controls.filtering
      const makeFilterGroup: FilterGroup | undefined = filterGroups.find((fg) => fg.name === 'make')

      if (makeFilterGroup && makeFilterGroup.filters.length > 0) {
        const filterValues: AttributeFilter[] = makeFilterGroup.filters
        const models = filterValues.find((x) => x.name === ATTRIBUTES.CAR_MODEL)?.values || []
        setCarModels(
          [...models].sort((a, b) => {
            if (a.label === 'Other' || a.label === 'Autre') return 1
            if (b.label === 'Other' || b.label === 'Autre') return -1
            return a.label.localeCompare(b.label)
          })
        )
      }

      setTotalResults(searchResultsPage.pagination.totalCount)
      setIsFetchingResults(false)
    } else {
      setCarModels([])
      setIsFetchingResults(false)
    }
  }

  const handleChangeCarModel = async (selectItem?: SelectItem) => {
    setSelectedCarModel(selectItem)
    setIsFetchingResults(true)

    trackEvent({
      action: GA_EVENT.FilterSelectBegin,
      label: getTrackingLabel({ model: selectItem?.value }),
    })

    const searchResultsPageByUrl = await fetchTotalResults({
      searchQuery,
      attributeFilter: {
        filterName: GPT_KEYS.CARMODEL_ATTRIBUTE_ID,
        values: [selectItem?.value as string],
      },
    })
    searchResultsPageByUrl && setTotalResults(searchResultsPageByUrl.pagination.totalCount)
    setIsFetchingResults(false)
  }

  const handleChangeMaxPrice = async (e: ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value)
    setMaxPrice(value)
  }

  const handleBlurMaxPrice = (e: FocusEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (!value) return

    trackEvent({
      action: GA_EVENT.FilterSelectBegin,
      label: getTrackingLabel({ price: value }),
    })
  }

  const handleKeyUpMaxPrice = useCallback(
    debounce(async (value: string, searchQuery: SearchQueryInput) => {
      setIsFetchingResults(true)
      const searchResultsPageByUrl = await fetchTotalResults({
        searchQuery,
        rangeFilter: {
          filterName: GPT_KEYS.PRICE_LABEL,
          minValue: 0,
          maxValue: value ? parseInt(value) * 100 : null,
        },
      })
      searchResultsPageByUrl && setTotalResults(searchResultsPageByUrl.pagination.totalCount)
      setIsFetchingResults(false)
    }, FIND_CAR_FILTERS_DEBOUNCE),
    []
  )

  const handleChangeMaxKilometers = (e: ChangeEvent<HTMLInputElement>) => {
    setMaxKilometers(parseInt(e.target.value))
  }

  const handleBlurMaxKilometers = (e: FocusEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (!value) return

    trackEvent({
      action: GA_EVENT.FilterSelectBegin,
      label: getTrackingLabel({ km: value }),
    })
  }

  const handleKeyUpMaxKilometers = useCallback(
    debounce(async (value: string, searchQuery: SearchQueryInput) => {
      setIsFetchingResults(true)
      const searchResultsPageByUrl = await fetchTotalResults({
        searchQuery,
        rangeFilter: {
          filterName: GPT_KEYS.CARMILEAGE_ATTRIBUTE_ID,
          minValue: 0,
          maxValue: parseInt(value) || null,
        },
      })
      searchResultsPageByUrl && setTotalResults(searchResultsPageByUrl.pagination.totalCount)
      setIsFetchingResults(false)
    }, FIND_CAR_FILTERS_DEBOUNCE),
    []
  )

  const handleSearchSubmit = async () => {
    const { data } = await fetchSeoUrl({ variables: { input: { searchQuery } } })

    if (data?.searchUrl) {
      trackEvent({
        action: GA_EVENT.FilterSelect,
        label: getTrackingLabel({}),
      })

      push(data.searchUrl)
    }
  }

  const formattedTotalResults = formatWholeNumber(totalResults, routeLocale === ROUTE_LOCALE.en)

  return (
    <FormWrapper>
      <GridItemWrapper gridAreaName="make">
        <Select
          bottom="0"
          error=""
          label={t('top_cars_trucks.find_car.form.makes')}
          id="makes-filter"
          onSelectChange={(selectItem) => {
            handleChangeCarMake(selectItem)
          }}
          options={makeOptions as SelectItem[]}
          selectedValue={selectedCarMake?.value}
          isFloatingDropdown
          data-testid="makes-filter"
        />
      </GridItemWrapper>
      <GridItemWrapper gridAreaName="model">
        <Select
          bottom="0"
          error=""
          label={t('top_cars_trucks.find_car.form.models')}
          id="models-filter"
          onSelectChange={(selectItem) => {
            handleChangeCarModel(selectItem)
          }}
          options={carModels}
          selectedValue={selectedCarModel?.value}
          isFloatingDropdown
          data-testid="models-filter"
        />
      </GridItemWrapper>
      <GridItemWrapper gridAreaName="price">
        <TextField
          bottom="0"
          id="price-filter"
          label={t('top_cars_trucks.find_car.form.price')}
          onChange={handleChangeMaxPrice}
          onBlur={handleBlurMaxPrice}
          onKeyUp={(e) => handleKeyUpMaxPrice(e.currentTarget.value, searchQuery)}
          startAdornment={
            <AutosPriceIcon
              aria-hidden
              color={theme.colors.grey.light1}
              stroke={theme.colors.grey.light1}
            />
          }
          type="number"
          value={maxPrice?.toString() || ''}
          data-testid="price-filter"
        />
      </GridItemWrapper>
      <GridItemWrapper gridAreaName="km">
        <TextField
          bottom="0"
          id="km-filter"
          label={t('top_cars_trucks.find_car.form.km')}
          onChange={handleChangeMaxKilometers}
          onBlur={handleBlurMaxKilometers}
          onKeyUp={(e) => handleKeyUpMaxKilometers(e.currentTarget.value, searchQuery)}
          startAdornment={
            <KmSimpleIcon
              aria-hidden
              color={theme.colors.grey.light1}
              stroke={theme.colors.grey.light1}
            />
          }
          type="number"
          value={maxKilometers?.toString() || ''}
          data-testid="km-filter"
        />
      </GridItemWrapper>
      <GridItemWrapper gridAreaName="button">
        <Button
          isFullWidth
          onClick={handleSearchSubmit}
          size="medium"
          type="submit"
          variant="primary"
          isLoading={isFetchingResults}
          data-testid="search-button"
        >
          {totalResults == 0
            ? t('top_cars_trucks.find_car.form.button.default')
            : totalResults == 1
              ? t('top_cars_trucks.find_car.form.button.one_result')
              : t('top_cars_trucks.find_car.form.button', { totalListings: formattedTotalResults })}
        </Button>
      </GridItemWrapper>
    </FormWrapper>
  )
}
